import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  IoSparklesOutline,
  IoFlashOutline,
  IoTrendingUpOutline,
  IoTrendingDownOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
  IoChatbubbleEllipsesOutline,
  IoStorefrontOutline,
  IoArrowForwardOutline,
  IoCubeOutline,
  IoStarOutline,
  IoCarOutline,
  IoConstructOutline,
  IoRefreshOutline,
} from 'react-icons/io5';
import type { DashboardAiWidgets } from '../../api';
import { api } from '../../api';
import { Button } from '../../design';

const RUB = (v: number) => Math.round(v).toLocaleString('ru-RU') + ' ₽';

// ─────────────────────────────────────────────────────────
// Admin: Брифинг для руководителя + Индекс здоровья + Решения + Прогноз
// ─────────────────────────────────────────────────────────
export function AdminAiPanel({ w }: { w: DashboardAiWidgets }) {
  const qc = useQueryClient();
  const recompute = useMutation({
    mutationFn: api.dashboardAiRecompute,
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['dashboard-ai-widgets'] }), 60_000),
  });

  return (
    <>
      {/* Брифинг для руководителя */}
      <div className="card ai-card ai-briefing-card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '20px 20px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span className="ai-tag"><IoSparklesOutline /> AI</span>
              <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>
                Брифинг для руководителя
              </h3>
            </div>
            {w.executiveBriefing ? (
              <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{w.executiveBriefing.text}</div>
            ) : (
              <div className="muted" style={{ fontSize: 13 }}>
                Брифинг ещё не построен. Нажмите «Обновить».
              </div>
            )}
            {w.executiveBriefing && (
              <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
                Обновлено {new Date(w.executiveBriefing.generatedAt).toLocaleString('ru-RU')} · {w.executiveBriefing.model}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={() => recompute.mutate()} loading={recompute.isPending} leftIcon={<IoRefreshOutline />}>
            Обновить
          </Button>
        </div>
      </div>

      {/* Индекс здоровья + Решения */}
      <div className="dashboard-row" style={{ marginBottom: 20 }}>
        {w.healthScore && <HealthScoreCard hs={w.healthScore} />}
        {w.decisions && w.decisions.length > 0 && <DecisionsCard items={w.decisions} />}
      </div>

      {/* Forecast */}
      {w.forecast && (
        <div className="dashboard-row" style={{ marginBottom: 20 }}>
          <ForecastCard f={w.forecast} />
        </div>
      )}
    </>
  );
}

function HealthScoreCard({ hs }: { hs: NonNullable<DashboardAiWidgets['healthScore']> }) {
  const color = hs.total >= 75 ? 'var(--green)' : hs.total >= 50 ? '#f59e0b' : 'var(--red)';
  const subs = [
    { label: 'Продажи', value: hs.sales },
    { label: 'Возвраты', value: hs.returns },
    { label: 'Остатки', value: hs.inventory },
    { label: 'Отзывы', value: hs.reviews },
  ];
  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ margin: 0, fontSize: 14 }}>Индекс здоровья</h3>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Сводная оценка бизнеса</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${color} ${hs.total * 3.6}deg, var(--border) 0deg)`, position: 'relative' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22, color }}>
            {hs.total}
          </div>
        </div>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {subs.map((s) => (
            <div key={s.label}>
              <div className="muted" style={{ fontSize: 11 }}>{s.label}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: s.value >= 75 ? 'var(--green)' : s.value >= 50 ? '#f59e0b' : 'var(--red)' }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DecisionsCard({ items }: { items: NonNullable<DashboardAiWidgets['decisions']> }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <h3 style={{ margin: 0, fontSize: 14 }}>Решения, ожидающие вас</h3>
      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{items.length} {items.length === 1 ? 'пункт' : 'пунктов'}</div>
      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
        {items.map((d) => (
          <Link key={d.type} to={d.link} className="decision-item">
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{d.label}</div>
            </div>
            <span className="badge badge-info">{d.count}</span>
            <IoArrowForwardOutline />
          </Link>
        ))}
      </div>
    </div>
  );
}

function ForecastCard({ f }: { f: NonNullable<DashboardAiWidgets['forecast']> }) {
  return (
    <div className="card" style={{ padding: 20, flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14 }}>Прогноз на 7 дней</h3>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>На базе ProductForecast</div>
        </div>
        <Link to="/ai" style={{ fontSize: 12 }}>Все прогнозы <IoArrowForwardOutline /></Link>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 14 }}>
        <ForecastStat icon={<IoWarningOutline />} value={f.stockoutRisk} label="Закончатся" color="var(--red)" />
        <ForecastStat icon={<IoTrendingUpOutline />} value={f.trendingUp} label="Рост спроса" color="var(--green)" />
        <ForecastStat icon={<IoTrendingDownOutline />} value={f.trendingDown} label="Падение" color="#94a3b8" />
      </div>
    </div>
  );
}

function ForecastStat({ icon, value, label, color }: { icon: React.ReactNode; value: number; label: string; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 20, color }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Manager: Действия дня + Цены + Топ-риски + Черновики + Точки роста
// ─────────────────────────────────────────────────────────
export function ManagerAiPanel({ w }: { w: DashboardAiWidgets }) {
  return (
    <>
      {w.actionItems && w.actionItems.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            <IoFlashOutline /> Что сделать сегодня
          </h3>
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            {w.actionItems.map((a) => (
              <Link key={a.type} to={a.link} className={`action-item priority-${a.priority}`}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{a.title}</div>
                </div>
                <IoArrowForwardOutline />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="dashboard-row" style={{ marginBottom: 20 }}>
        {w.topRiskSku && w.topRiskSku.length > 0 && (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>SKU с высоким риском возврата</h3>
              <Link to="/ai" style={{ fontSize: 12 }}>Все <IoArrowForwardOutline /></Link>
            </div>
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {w.topRiskSku.map((r) => (
                <div key={r.productId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: 'var(--bg)', borderRadius: 6 }}>
                  <span className="risk-score risk-high" style={{ width: 32, height: 32, fontSize: 12 }}>{r.score}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name ?? '—'}</div>
                    {r.topCategory && <div className="muted" style={{ fontSize: 11 }}>Причина: {r.topCategory}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {w.opportunities && w.opportunities.length > 0 && (
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h3 style={{ margin: 0, fontSize: 14 }}><IoTrendingUpOutline /> Точки роста</h3>
              <Link to="/ai" style={{ fontSize: 12 }}>Все <IoArrowForwardOutline /></Link>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Растущий спрос + мало остатка</div>
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {w.opportunities.map((o) => (
                <div key={o.productId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: 'var(--bg)', borderRadius: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name ?? '—'}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Прогноз 7д: <strong>{o.predicted7d}</strong> · Хватит на {o.daysOfStock?.toFixed(1)} дн
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {w.pricingAlerts && w.pricingAlerts.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Цены: дороже рынка</h3>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Топ-5 товаров с самым большим отрывом</div>
          <table className="products" style={{ marginTop: 12 }}>
            <thead>
              <tr><th>Товар</th><th className="num">Наша цена</th><th className="num">Мин. рынок</th><th className="num">Δ</th></tr>
            </thead>
            <tbody>
              {w.pricingAlerts.map((p) => (
                <tr key={p.productId}>
                  <td style={{ fontSize: 12 }}>{p.name}</td>
                  <td className="num">{RUB(p.ourPrice)}</td>
                  <td className="num">{RUB(p.mpMinPrice)}</td>
                  <td className="num" style={{ color: 'var(--red)' }}>+{RUB(p.ourPrice - p.mpMinPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {w.reviewDrafts && w.reviewDrafts.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={{ margin: 0, fontSize: 14 }}><IoStarOutline /> Готовые AI-черновики ответов</h3>
            <Link to="/reviews" style={{ fontSize: 12 }}>Все отзывы <IoArrowForwardOutline /></Link>
          </div>
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            {w.reviewDrafts.map((r) => (
              <ReviewDraftCard key={r.id} r={r} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function ReviewDraftCard({ r }: { r: NonNullable<DashboardAiWidgets['reviewDrafts']>[number] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 14 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
        <span className="muted" style={{ fontSize: 11 }}>SKU {r.sku}</span>
      </div>
      <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{r.text}</div>
      {r.aiDraft && (
        <>
          <button className="link-btn" style={{ marginTop: 8, fontSize: 12 }} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Скрыть' : 'Показать'} AI-ответ
          </button>
          {expanded && (
            <div style={{ marginTop: 8, padding: 10, background: 'var(--accent-soft)', borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}>
              <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> {r.aiDraft}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Warehouse: Stockouts + Incoming Returns + Picking + Image flags
// ─────────────────────────────────────────────────────────
export function WarehouseAiPanel({ w }: { w: DashboardAiWidgets }) {
  return (
    <>
      {w.stockoutWarnings && w.stockoutWarnings.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14 }}><IoWarningOutline style={{ color: 'var(--red)' }} /> Скоро закончится</h3>
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>AI-прогноз — заказать в 1С</div>
            </div>
          </div>
          <table className="products" style={{ marginTop: 12 }}>
            <thead>
              <tr><th>Товар</th><th className="num">Остаток</th><th className="num">Прогноз 7д</th><th className="num">Хватит дней</th></tr>
            </thead>
            <tbody>
              {w.stockoutWarnings.map((s) => (
                <tr key={s.productId}>
                  <td style={{ fontSize: 12 }}>
                    {s.name ?? '—'}
                    <div className="muted" style={{ fontSize: 11 }}>{s.offerId}</div>
                  </td>
                  <td className="num">{s.stockNow}</td>
                  <td className="num">{s.predicted7d}</td>
                  <td className="num" style={{ color: 'var(--red)', fontWeight: 600 }}>
                    {s.daysOfStock != null ? s.daysOfStock.toFixed(1) : '0'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {w.incomingReturns && w.incomingReturns.count > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}><IoCarOutline /> Входящие возвраты</h3>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {w.incomingReturns.count} возвратов в пути или ожидают забора
          </div>
          {w.incomingReturns.byCategory.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {w.incomingReturns.byCategory.map((c) => (
                <span key={c.category} className="badge badge-info">
                  {c.category}: <strong>{c.count}</strong>
                </span>
              ))}
            </div>
          )}
          <Link to="/returns" className="btn ghost" style={{ marginTop: 12, display: 'inline-flex' }}>
            К возвратам <IoArrowForwardOutline />
          </Link>
        </div>
      )}

      {w.imageFlags && w.imageFlags.count > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--yellow-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IoConstructOutline style={{ fontSize: 20, color: '#92400e' }} />
            <div style={{ flex: 1 }}>
              <strong>Vision AI:</strong> {w.imageFlags.count} товаров с проблемами фото —
              можно поручить пересъёмку при приёмке
            </div>
            <Link to="/ai" className="btn ghost">Подробнее</Link>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Viewer: Weekly Digest + Market Trend
// ─────────────────────────────────────────────────────────
export function ViewerAiPanel({ w }: { w: DashboardAiWidgets }) {
  return (
    <>
      {w.weeklyDigest && (
        <div className="card ai-card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span className="ai-tag"><IoSparklesOutline /> AI</span>
            <h3 style={{ margin: 0, fontSize: 14 }}>Еженедельный дайджест</h3>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{w.weeklyDigest.summary}</div>
          {w.weeklyDigest.highlights.length > 0 && (
            <div style={{ display: 'grid', gap: 6, marginTop: 14 }}>
              {w.weeklyDigest.highlights.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg)', fontSize: 13 }}>
                  <span>{highlightIcon(h.type)}</span>
                  <span>{h.text}</span>
                </div>
              ))}
            </div>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>
            Обновлено {new Date(w.weeklyDigest.generatedAt).toLocaleString('ru-RU')}
          </div>
        </div>
      )}

      {w.marketTrend && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Наша позиция на рынке</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 14 }}>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Наш рост vs прошл. неделя</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: w.marketTrend.ourGrowthPct == null ? 'var(--text-muted)' : w.marketTrend.ourGrowthPct > 0 ? 'var(--green)' : 'var(--red)' }}>
                {w.marketTrend.ourGrowthPct == null ? '—' : `${w.marketTrend.ourGrowthPct > 0 ? '+' : ''}${w.marketTrend.ourGrowthPct}%`}
              </div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Дороже рынка (yellow/red)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--red)' }}>{w.marketTrend.ourYellowRed}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>Выгоднее рынка (green)</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>{w.marketTrend.ourGreen}</div>
            </div>
          </div>
          {w.marketTrend.competitorSummary && (
            <div style={{ marginTop: 14, padding: 12, background: 'var(--bg)', borderRadius: 6, fontSize: 13, lineHeight: 1.5 }}>
              <IoSparklesOutline style={{ verticalAlign: 'middle' }} /> {w.marketTrend.competitorSummary}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function highlightIcon(t: string): string {
  if (t === 'win') return '✅';
  if (t === 'concern') return '⚠️';
  if (t === 'opportunity') return '💡';
  if (t === 'trend') return '📈';
  return '•';
}

// ─────────────────────────────────────────────────────────
// Embedded mini-chat (для viewer)
// ─────────────────────────────────────────────────────────
export function EmbeddedChat() {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button className="ai-fab" onClick={() => setOpen(true)}>
        <IoSparklesOutline /> Спросить AI
      </button>
    );
  }

  return (
    <div className="ai-fab-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong>Быстрый вопрос AI-ассистенту</strong>
        <Button variant="ghost" style={{ padding: 4 }} onClick={() => setOpen(false)} iconOnly aria-label="Закрыть">✕</Button>
      </div>
      <textarea
        className="chat-input"
        rows={3}
        placeholder="Например: топ возвратов за месяц"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        style={{ width: '100%' }}
      />
      <Link
        to={`/chat${input.trim() ? `?msg=${encodeURIComponent(input)}` : ''}`}
        className="btn primary"
        style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
      >
        Открыть в чате <IoArrowForwardOutline />
      </Link>
    </div>
  );
}

// Mini quick links для warehouse stockouts CTA в верхней секции
export const _unused = { IoStorefrontOutline, IoCubeOutline, IoCheckmarkCircleOutline };
